/*
 * One clock for the whole app, so it can be moved in development.
 *
 * Nearly every screen here is a function of the current time — which session is live,
 * whether the check-in button shows, which day the programme opens on, whether the
 * certificate is claimable. None of that can be looked at once the event is in the
 * past, and the seeded event ends on 26 August. The live bulletin in particular went
 * unverified through four palettes and two design branches for exactly this reason:
 * there was no way to make a slot be happening now.
 *
 * So every clock read goes through `nowMs()`, and in a development build only, an
 * offset can be applied:
 *
 *     ?now=2026-08-25T10:15        (Malaysian local time, as you would say it)
 *     localStorage['cpd.dev.now']  (same value, survives navigation)
 *
 * `import.meta.env.DEV` is a literal at build time, so the whole override is removed
 * from a production bundle by dead-code elimination — `nowMs()` compiles down to
 * `Date.now()`. There is no runtime switch a delegate could reach.
 *
 * This is a *display* clock and nothing more. It does not touch what the API is told:
 * a check-in posted while the clock is moved is still recorded at the real time by the
 * server, which is the only clock that counts for attendance.
 */

const STORAGE_KEY = 'cpd.dev.now'

/** Milliseconds to add to the real clock. Always 0 outside development. */
let offsetMs = 0

/**
 * "2026-08-25T10:15" means 10:15 in Kuala Lumpur, because that is how anyone reading
 * the programme would say it. Parsing it as UTC would put you eight hours out and make
 * the whole tool lie in the same direction the server's own date bug does.
 */
function parseMytLocal(value: string): number | null {
  const match = value.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
  )
  if (!match) return null
  const [, y, mo, d, h = '00', mi = '00', s = '00'] = match
  const ms = Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${s}+08:00`)
  return Number.isNaN(ms) ? null : ms
}

if (import.meta.env.DEV) {
  const fromQuery = new URLSearchParams(window.location.search).get('now')
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Private mode, or storage disabled. A missing override is the correct fallback.
  }

  const raw = fromQuery ?? stored
  if (raw === 'off' || raw === '') {
    try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* nothing to undo */ }
  } else if (raw) {
    const target = parseMytLocal(raw)
    if (target === null) {
      console.warn(`[clock] Ignoring ?now=${raw} — expected YYYY-MM-DD or YYYY-MM-DDTHH:MM.`)
    } else {
      offsetMs = target - Date.now()
      // Persist only what came from the URL, so a bad stored value cannot outlive itself.
      if (fromQuery) {
        try { window.localStorage.setItem(STORAGE_KEY, fromQuery) } catch { /* not essential */ }
      }
      console.warn(
        `[clock] Development clock moved to ${new Date(target).toISOString()} ` +
        `(${(offsetMs / 3_600_000).toFixed(1)}h from real). ?now=off to clear.`,
      )
    }
  }
}

/** The current time, in milliseconds. The only clock the UI should read. */
export function nowMs(): number {
  return Date.now() + offsetMs
}

/** Whether a development override is in force. Always false in production. */
export function isClockShifted(): boolean {
  return offsetMs !== 0
}
